// EmployeeProfileWizard.jsx
// Shown to an employee the first time they log in (profile_completed_at is null).
// The wizard is full-screen and blocks access to the rest of the app until done.
// Steps:
//   1 – Welcome
//   2 – Personal details (photo, DOB, phone, address, emergency contact)
//   3 – Professional profile (bio, skills, LinkedIn, certifications)
//   4 – Documents (resume / CV upload)
//   5 – Done

import React, { useRef, useState } from 'react';
import {
  Camera, Upload, CheckCircle, ChevronRight, ChevronLeft,
  User, Briefcase, FileText, Loader, X, Plus, Trash2,
  Phone, AlertCircle, Linkedin,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  updateEmployeeProfile,
  uploadEmployeePhoto,
  uploadEmployeeResume,
} from '../lib/db';
import { validatePhone } from '../lib/phoneValidation';

// ─── tiny helpers ─────────────────────────────────────────────────────────────
const cx = (...cls) => cls.filter(Boolean).join(' ');

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
      <AlertCircle size={11} /> {msg}
    </p>
  );
}

function Label({ children, required }) {
  return (
    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  );
}

function Input({ className, ...props }) {
  return (
    <input
      {...props}
      className={cx(
        'w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg',
        'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100',
        'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent',
        'disabled:opacity-50',
        className,
      )}
    />
  );
}

function Textarea({ className, ...props }) {
  return (
    <textarea
      {...props}
      className={cx(
        'w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg',
        'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100',
        'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent',
        'resize-none',
        className,
      )}
    />
  );
}

// ─── Step indicator ────────────────────────────────────────────────────────────
const STEPS = [
  { label: 'Welcome',      icon: User },
  { label: 'Personal',     icon: User },
  { label: 'Professional', icon: Briefcase },
  { label: 'Documents',    icon: FileText },
  { label: 'Done',         icon: CheckCircle },
];

function StepBar({ current }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {STEPS.map((s, i) => {
        const done    = i < current;
        const active  = i === current;
        const last    = i === STEPS.length - 1;
        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center">
              <div
                className={cx(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition',
                  done   ? 'bg-indigo-600 text-white'
                  : active ? 'bg-indigo-600 text-white ring-4 ring-indigo-100 dark:ring-indigo-900/40'
                  :          'bg-slate-200 dark:bg-slate-700 text-slate-500',
                )}
              >
                {done ? <CheckCircle size={14} /> : i + 1}
              </div>
              <span className={cx(
                'mt-1 text-[10px] font-medium hidden sm:block',
                active ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400',
              )}>
                {s.label}
              </span>
            </div>
            {!last && (
              <div className={cx(
                'h-0.5 w-8 sm:w-12 mx-1 mb-4 transition',
                done ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-700',
              )} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Photo picker ──────────────────────────────────────────────────────────────
function PhotoPicker({ value, onChange }) {
  const fileRef   = useRef(null);
  const cameraRef = useRef(null);
  const [preview, setPreview] = useState(value || null);

  const pick = (file) => {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    onChange(file);
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className={cx(
          'w-28 h-28 rounded-full border-2 border-dashed border-slate-300 dark:border-slate-600',
          'flex items-center justify-center overflow-hidden bg-slate-50 dark:bg-slate-800',
        )}
      >
        {preview
          ? <img src={preview} alt="Profile" className="w-full h-full object-cover" />
          : <User size={36} className="text-slate-300 dark:text-slate-600" />
        }
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition"
        >
          <Upload size={12} /> Upload photo
        </button>
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition"
        >
          <Camera size={12} /> Camera
        </button>
      </div>
      {/* Two separate inputs — changing capture at runtime is unreliable */}
      <input ref={fileRef}   type="file" accept="image/*"            className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
      <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
      {preview && (
        <button
          type="button"
          onClick={() => { setPreview(null); onChange(null); }}
          className="text-xs text-slate-400 hover:text-red-500 flex items-center gap-1"
        >
          <X size={11} /> Remove photo
        </button>
      )}
    </div>
  );
}

// ─── Tag input (skills / certifications) ───────────────────────────────────────
function TagInput({ value = [], onChange, placeholder }) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const tag = draft.trim();
    if (!tag || value.includes(tag)) { setDraft(''); return; }
    onChange([...value, tag]);
    setDraft('');
  };

  const remove = (tag) => onChange(value.filter((t) => t !== tag));

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={add}
          className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 transition"
        >
          <Plus size={14} />
        </button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium"
            >
              {tag}
              <button type="button" onClick={() => remove(tag)} className="hover:text-red-500">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Resume picker ─────────────────────────────────────────────────────────────
function ResumePicker({ value, onChange }) {
  const ref = useRef(null);
  return (
    <div>
      {value ? (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <FileText size={20} className="text-indigo-500 flex-shrink-0" />
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate flex-1">{value.name}</p>
          <button type="button" onClick={() => onChange(null)} className="text-slate-400 hover:text-red-500">
            <Trash2 size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className={cx(
            'w-full flex flex-col items-center gap-2 p-6 rounded-xl border-2 border-dashed',
            'border-slate-300 dark:border-slate-600 hover:border-indigo-400 dark:hover:border-indigo-500',
            'text-slate-500 dark:text-slate-400 transition cursor-pointer',
          )}
        >
          <Upload size={24} />
          <p className="text-sm font-medium">Click to upload resume / CV</p>
          <p className="text-xs text-slate-400">PDF, DOC, or DOCX — max 10 MB</p>
        </button>
      )}
      <input
        ref={ref}
        type="file"
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
    </div>
  );
}

// ─── Main wizard ───────────────────────────────────────────────────────────────
export default function EmployeeProfileWizard({ employee, onComplete }) {
  // eslint-disable-next-line no-unused-vars
  const { userProfile: _userProfile } = useAuth();
  const [step, setStep] = useState(0); // 0=welcome, 1=personal, 2=professional, 3=documents, 4=done

  // Form state
  const [photoFile,   setPhotoFile]   = useState(null);
  const [resumeFile,  setResumeFile]  = useState(null);
  const [personal, setPersonal] = useState({
    phone:                   employee?.phone                  || '',
    date_of_birth:           employee?.date_of_birth          || '',
    address:                 employee?.address                || '',
    emergency_contact_name:  employee?.emergency_contact_name  || '',
    emergency_contact_phone: employee?.emergency_contact_phone || '',
  });
  const [professional, setProfessional] = useState({
    bio:              employee?.bio             || '',
    skills:           employee?.skills          || [],
    linkedin_url:     employee?.linkedin_url    || '',
    certifications:   employee?.certifications  || [],
  });

  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [saveErr, setSaveErr] = useState('');

  // ── validation ──
  const validatePersonal = () => {
    const e = {};
    // Primary phone — required.
    const primary = validatePhone(personal.phone, { required: true });
    if (!primary.valid) e.phone = primary.error;
    // Emergency contact phone — optional. Only complain if they DID
    // type something but it's not parseable as a real number.
    if (personal.emergency_contact_phone?.trim()) {
      const emerg = validatePhone(personal.emergency_contact_phone, { required: false });
      if (!emerg.valid) e.emergency_contact_phone = emerg.error;
    }
    return e;
  };

  // ── navigation ──
  const next = () => {
    if (step === 1) {
      const e = validatePersonal();
      if (Object.keys(e).length) { setErrors(e); return; }
      setErrors({});
    }
    setStep((s) => s + 1);
  };

  const back = () => { setErrors({}); setStep((s) => s - 1); };

  // ── submit ──
  const handleFinish = async () => {
    setSaving(true);
    setSaveErr('');
    try {
      // Normalise empty strings → null before sending. Postgres rejects
      // empty strings cast to date/timestamptz types (we hit this with
      // a blank date_of_birth: "" causing 'invalid input syntax for
      // type date'). Migration 135 fixed the server side with nullif(),
      // but the client-side strip means future date columns won't
      // re-trip the same bug if the RPC ever drifts.
      const blanksToNull = (obj) => {
        const out = {};
        for (const [k, v] of Object.entries(obj)) {
          out[k] = (typeof v === 'string' && v.trim() === '') ? null : v;
        }
        return out;
      };
      // Canonicalise phone numbers before storage. Same number typed
      // as "024 123 4567", "+233 24 123 4567", or "0241234567" should
      // all land in the DB as "+233241234567" — otherwise search and
      // dedup are useless.
      const cleanedPersonal = blanksToNull(personal);
      const primaryPhone = validatePhone(cleanedPersonal.phone || '', { required: true });
      if (primaryPhone.normalized) cleanedPersonal.phone = primaryPhone.normalized;
      const emergPhone = validatePhone(cleanedPersonal.emergency_contact_phone || '', { required: false });
      if (emergPhone.normalized) cleanedPersonal.emergency_contact_phone = emergPhone.normalized;

      const payload = {
        ...cleanedPersonal,
        ...blanksToNull(professional),
        profile_completed_at: new Date().toISOString(),
      };

      // Upload photo if chosen
      if (photoFile) {
        const { url, error: photoErr } = await uploadEmployeePhoto(
          employee.firm_id, employee.id, photoFile,
        );
        if (photoErr) throw photoErr;
        payload.profile_photo_url = url;
      }

      // Upload resume if chosen
      if (resumeFile) {
        const { path, error: resumeErr } = await uploadEmployeeResume(
          employee.firm_id, employee.id, resumeFile,
        );
        if (resumeErr) throw resumeErr;
        payload.resume_url = path; // store the storage path; generate signed URLs on demand
      }

      const { error } = await updateEmployeeProfile(employee.id, payload);
      if (error) throw error;

      setStep(4);
      // Give the "Done" screen a moment, then hand off
      setTimeout(() => onComplete({ ...employee, ...payload }), 1800);
    } catch (err) {
      setSaveErr(err.message || 'Could not save profile. Please try again.');
      setSaving(false);
    }
  };

  // ── skip (optional) ──
  // "Skip for now" used to write profile_completed_at — that silently
  // marked the profile as done forever, hiding the reminder. Now skip
  // just defers within this session (sessionStorage flag). The profile
  // wizard returns on next login until they actually complete it, and a
  // reminder banner shows on the dashboard so they don't forget.
  const handleSkip = () => {
    try { sessionStorage.setItem(`lf_profile_wizard_skipped_${employee.id}`, '1'); }
    catch { /* private mode — fine */ }
    onComplete(employee); // hand control back; profile_completed_at stays null
  };

  // ── renders ──
  const displayName = [employee?.first_name, employee?.last_name].filter(Boolean).join(' ') || 'there';

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-xl">

        {/* Logo / header */}
        <div className="text-center mb-6">
          <img src="/logo.png" alt="DocketFlow" className="mx-auto h-16 w-auto mb-2 drop-shadow" />
          <p className="text-slate-500 dark:text-slate-400 text-sm">Complete your employee profile</p>
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6 sm:p-8">
          {step < 4 && <StepBar current={step} />}

          {/* ── Step 0: Welcome ── */}
          {step === 0 && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mx-auto">
                <User size={28} className="text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Welcome, {displayName}!</h2>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                  Before you start, take a few minutes to complete your employee profile.
                  This helps your colleagues know who you are and keeps your records up to date.
                </p>
              </div>
              <ul className="text-left space-y-2 text-sm text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                {[
                  'Profile photo',
                  'Personal details & emergency contact',
                  'Bio, skills & professional info',
                  'Resume / CV upload',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-indigo-500 flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={next}
                  className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition"
                >
                  Get started <ChevronRight size={16} />
                </button>
                <button
                  onClick={handleSkip}
                  disabled={saving}
                  className="flex-1 px-5 py-2.5 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {/* ── Step 1: Personal details ── */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Personal details</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Tell us a bit about yourself.</p>
              </div>

              {/* Photo */}
              <div>
                <Label>Profile photo</Label>
                <PhotoPicker value={null} onChange={setPhotoFile} />
              </div>

              {/* Phone */}
              <div>
                <Label required>Phone number</Label>
                <Input
                  type="tel"
                  value={personal.phone}
                  onChange={(e) => {
                    setPersonal((p) => ({ ...p, phone: e.target.value }));
                    setErrors((err) => ({ ...err, phone: undefined }));
                  }}
                  placeholder="+233 20 000 0000"
                  className={errors.phone ? 'ring-1 ring-red-400 border-red-400' : ''}
                />
                <FieldError msg={errors.phone} />
              </div>

              {/* Date of birth */}
              <div>
                <Label>Date of birth</Label>
                <Input
                  type="date"
                  value={personal.date_of_birth}
                  onChange={(e) => setPersonal((p) => ({ ...p, date_of_birth: e.target.value }))}
                />
              </div>

              {/* Address */}
              <div>
                <Label>Home address</Label>
                <Textarea
                  rows={2}
                  value={personal.address}
                  onChange={(e) => setPersonal((p) => ({ ...p, address: e.target.value }))}
                  placeholder="Street, city, region / state"
                />
              </div>

              {/* Emergency contact */}
              <div>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                  <Phone size={13} /> Emergency contact
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={personal.emergency_contact_name}
                      onChange={(e) => setPersonal((p) => ({ ...p, emergency_contact_name: e.target.value }))}
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      type="tel"
                      value={personal.emergency_contact_phone}
                      onChange={(e) => setPersonal((p) => ({ ...p, emergency_contact_phone: e.target.value }))}
                      placeholder="+233 20 000 0000"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Professional ── */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Professional profile</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Share your professional background and skills.
                </p>
              </div>

              {/* Bio */}
              <div>
                <Label>Bio / About me</Label>
                <Textarea
                  rows={4}
                  value={professional.bio}
                  onChange={(e) => setProfessional((p) => ({ ...p, bio: e.target.value }))}
                  placeholder="A short summary of your background and expertise…"
                />
              </div>

              {/* Skills */}
              <div>
                <Label>Skills</Label>
                <TagInput
                  value={professional.skills}
                  onChange={(v) => setProfessional((p) => ({ ...p, skills: v }))}
                  placeholder="e.g. Contract law, Negotiation — press Enter to add"
                />
              </div>

              {/* LinkedIn */}
              <div>
                <Label>LinkedIn URL</Label>
                <div className="relative">
                  <Linkedin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input
                    className="pl-8"
                    type="url"
                    value={professional.linkedin_url}
                    onChange={(e) => setProfessional((p) => ({ ...p, linkedin_url: e.target.value }))}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
              </div>

              {/* Certifications */}
              <div>
                <Label>Certifications / qualifications</Label>
                <TagInput
                  value={professional.certifications}
                  onChange={(v) => setProfessional((p) => ({ ...p, certifications: v }))}
                  placeholder="e.g. LLB, MBA — press Enter to add"
                />
              </div>
            </div>
          )}

          {/* ── Step 3: Documents ── */}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Resume / CV</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Upload your current resume or CV so it's on file.
                </p>
              </div>
              <ResumePicker value={resumeFile} onChange={setResumeFile} />
              {saveErr && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <AlertCircle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 dark:text-red-400">{saveErr}</p>
                </div>
              )}
            </div>
          )}

          {/* ── Step 4: Done ── */}
          {step === 4 && (
            <div className="text-center space-y-4 py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
                <CheckCircle size={32} className="text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Profile complete!</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
                Your profile has been saved. Taking you to the app…
              </p>
              <Loader size={20} className="animate-spin text-indigo-500 mx-auto" />
            </div>
          )}

          {/* ── Navigation buttons ── */}
          {step > 0 && step < 4 && (
            <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={back}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition"
              >
                <ChevronLeft size={15} /> Back
              </button>

              {step < 3 ? (
                <button
                  type="button"
                  onClick={next}
                  className="flex items-center gap-1.5 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition"
                >
                  Continue <ChevronRight size={15} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleFinish}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-xl text-sm font-medium transition"
                >
                  {saving ? (
                    <><Loader size={14} className="animate-spin" /> Saving…</>
                  ) : (
                    <><CheckCircle size={14} /> Save &amp; finish</>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-slate-400 dark:text-slate-600 text-xs mt-6">© Phoenix Tech · DocketFlow</p>
      </div>
    </div>
  );
}
